import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';

import { selectedIncomeSplitMonthAtom } from '../state/IncomeSplitAtoms';
import { type IncomeSplitProgress } from '../types/IncomeSplitOverviewTypes';
import { IncomeSplitProgressOverview } from './IncomeSplitProgressOverview';

export const IncomeSplitExecutionCarousel = ({
    initialIndex = 0,
    setCurrentExecutionIndex,
}: {
    initialIndex?: number;
    setCurrentExecutionIndex: (index: number) => void;
}) => {
    const [index, setIndex] = useState(initialIndex);
    const [animationEnabled, setAnimationEnabled] = useState(false);

    const selectedIncomeSplitMonth = useAtomValue(selectedIncomeSplitMonthAtom);

    const routes = Object.values(
        selectedIncomeSplitMonth?.executions ?? [],
    ).map((execution) => ({
        key: execution.id,
    }));

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: string } }) => {
        const executionIndex = selectedIncomeSplitMonth?.executions.findIndex(
            (execution) => execution.id === route.key,
        );
        if (executionIndex == null || executionIndex < 0) return null;
        const execution = selectedIncomeSplitMonth?.executions[executionIndex];
        if (execution == null) return null;

        let previousProgress: IncomeSplitProgress | undefined;
        if (executionIndex > 0) {
            previousProgress =
                selectedIncomeSplitMonth?.executions[executionIndex - 1]
                    ?.progress;
        }
        return (
            <IncomeSplitProgressOverview
                progress={execution.progress}
                previousProgress={previousProgress}
                thisExecutionProgress={execution.thisExecutionProgress}
            />
        );
    };

    const handleIndexChange = (newIndex: number) => {
        setIndex(newIndex);
        setCurrentExecutionIndex(newIndex);
        // Enable animations after first user interaction
        if (!animationEnabled) {
            setAnimationEnabled(true);
        }
    };

    // sync index when initialIndex changes (from left/right buttons)
    useEffect(() => {
        if (initialIndex !== index) {
            setIndex(initialIndex);
            // Enable animations after programmatic index change
            if (!animationEnabled) {
                setAnimationEnabled(true);
            }
        }
    }, [initialIndex, index]);

    // Enable animations after component mounts (allows initial render without animation)
    // Reset animation state when component mounts with new key or when execution changes
    useEffect(() => {
        setAnimationEnabled(false);
        // Longer delay on web to prevent flashing
        const delay = Platform.OS === 'web' ? 300 : 200;
        const timer = setTimeout(() => {
            setAnimationEnabled(true);
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [selectedIncomeSplitMonth?.month, initialIndex]);

    // Don't render TabView if no routes
    if (routes.length === 0) {
        return null;
    }

    return (
        <TabView
            swipeEnabled={true}
            animationEnabled={animationEnabled}
            navigationState={{ index, routes }}
            onIndexChange={handleIndexChange}
            renderScene={renderScene}
            renderTabBar={() => <></>}
            style={{ width: '100%', height: 130 }}
        />
    );
};
